// Creating channels and spaces (09-create.md): the short "Create channel" dialog inside a space, and
// the full form on Home's page and the "Create space" page. "Ask to join", "More options" and the
// voice type on Home's page were candidates to drop; what is left is a name, a topic, end-to-end
// encryption, and a space's access (Everyone in <Circle>, Private, Public). A new space gets its
// first channel "General" and opens on it.
import { EventType, JoinRule, type MatrixClient, type Room } from 'matrix-js-sdk';
import { useEffect, useState, type FormEvent } from 'react';
import { circleName } from '../accounts/accounts';
import { Button } from '../blocks/Button';
import { Dialog, DialogButtons, ErrorLine } from '../blocks/Dialog';
import { Field, Switch } from '../blocks/Field';
import { Icon, icons } from '../blocks/icons';
import { toast } from '../blocks/Toast';
import { useAction } from '../blocks/Wait';
import { describe } from '../core/errors';
import { navigate } from '../core/router';
import { roomPath } from '../core/routes';
import { server } from '../core/server';
import { serverOf } from '../core/session';
import { t } from '../i18n';
import { refFor, viaServers } from '../session/rooms';
import { CircleSpaces } from './CircleSpaces';
import { frameIcons } from './icons';

const CALL_ROOM = 'org.matrix.msc3417.call';
const MAX = 32;
type Access = 'circle' | 'invite' | 'public';

/** The server's default room version as a number ("1" when missing; others count as 0). */
const roomVersion = () => {
  const version = (server.capabilities as { 'm.room_versions'?: { default?: string } } | undefined)?.['m.room_versions']?.default ?? '1';
  return { version, number: Number(version) || 0 };
};

export interface NewRoom {
  name: string;
  topic?: string;
  kind: 'text' | 'voice' | 'space';
  encrypted: boolean;
  /** A space's access (09, The full form 2); a channel's follows from `space`. */
  access?: Access;
  /** Made in this space: joinable by its members where the room version allows it, and linked. */
  space?: { roomId: string };
  /** A direct chat (17's "New message"): these people are invited and it goes into m.direct. */
  invite?: string[];
}

/** The room-making steps (09, "Making the room" 1–2); also used by 17's "New message". */
export async function makeRoom(client: MatrixClient, room: NewRoom): Promise<string> {
  const { version, number } = roomVersion();
  const restricted = !!room.space && number >= 8;
  // Everyone in <Circle>: public, never federated, listed in this server's room directory (07, "Spaces in <Circle>").
  const circle = room.access === 'circle';
  const space = room.space && client.getRoom(room.space.roomId);
  const initial_state: { type: string; state_key: string; content: object }[] = [];
  if (room.encrypted && room.kind !== 'space') initial_state.push({ type: EventType.RoomEncryption, state_key: '', content: { algorithm: 'm.megolm.v1.aes-sha2' } });
  if (room.space) initial_state.push({ type: EventType.SpaceParent, state_key: room.space.roomId, content: { canonical: true, via: space ? viaServers(space) : [serverOf(client.getUserId()!)] } });
  if (room.kind === 'voice') initial_state.push({ type: 'org.matrix.msc3401.call', state_key: '', content: {} });
  initial_state.push({
    type: EventType.RoomJoinRules,
    state_key: '',
    content: restricted ? { join_rule: JoinRule.Restricted, allow: [{ type: 'm.room_membership', room_id: room.space!.roomId }] } : { join_rule: circle || room.access === 'public' ? JoinRule.Public : JoinRule.Invite },
  });
  const { room_id } = await client.createRoom({
    room_version: version,
    ...(room.name ? { name: room.name } : {}),
    ...(room.topic ? { topic: room.topic } : {}),
    creation_content: {
      ...(room.kind === 'space' ? { type: 'm.space' } : room.kind === 'voice' ? { type: CALL_ROOM } : {}),
      ...(number >= 12 ? { additional_creators: [] } : {}),
      ...(circle ? { 'm.federate': false } : {}),
    },
    power_level_content_override: room.kind === 'space' ? { events_default: 50, events: { 'chat.desyr.banner': 100 } } : { events: { 'org.matrix.msc3401.call.member': 0 } },
    initial_state,
    ...(room.invite ? { invite: room.invite, is_direct: true, preset: 'trusted_private_chat', visibility: 'private' } : {}),
    ...(circle ? { visibility: 'public' } : {}),
  } as never);
  return room_id;
}

/** Linking a new channel into its space (09, step 3). */
const link = (client: MatrixClient, spaceId: string, roomId: string) =>
  client.sendStateEvent(spaceId, EventType.SpaceChild, { suggested: false, via: [serverOf(client.getUserId()!)], auto_join: false } as never, roomId);

/** Makes, links and opens; a link that fails keeps the made room for "Add it to the space". */
function useCreate(client: MatrixClient, onMade: (roomId: string, stayed: boolean) => void | Promise<void>) {
  const [unlinked, setUnlinked] = useState<{ roomId: string; reason: string }>();
  const action = useAction(async (room: NewRoom) => {
    const at = location.pathname;
    const roomId = unlinked?.roomId ?? (await makeRoom(client, room));
    if (room.space) {
      try {
        await link(client, room.space.roomId, roomId);
      } catch (e) {
        setUnlinked({ roomId, reason: describe(e, 'save').text });
        return;
      }
    }
    setUnlinked(undefined);
    // Left meanwhile (the browser's Back): it is made and listed but doesn't open (07, v1.58.38).
    await onMade(roomId, location.pathname === at);
  }, 'save');
  const message = unlinked && !action.busy ? `${t('create.notLinked')} ${unlinked.reason}` : action.message;
  return { ...action, message, unlinked: !!unlinked };
}

const opened = (space: Room | undefined, roomId: string) => navigate(space ? roomPath({ space: refFor(space) }, roomId) : roomPath({}, roomId));

/** The short dialog in a space (09, "The short dialog"). */
export function CreateChannelDialog({ client, space, onClose }: { client: MatrixClient; space: Room; onClose: () => void }) {
  const [name, setName] = useState('');
  const [voice, setVoice] = useState(false);
  const { run, busy, message, unlinked } = useCreate(client, (roomId) => (onClose(), opened(space, roomId)));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) void run({ name: name.trim(), kind: voice ? 'voice' : 'text', encrypted: true, space });
  };
  return (
    <Dialog title={t('create.channel')} focusFirst onClose={onClose}>
      <form onSubmit={submit}>
        <Field label={t('create.channelName')} value={name} maxLength={MAX} count={`${name.length}/${MAX}`} disabled={busy} autoFocus onChange={(e) => setName(e.target.value)} />
        <fieldset className="radio-group" disabled={busy}>
          <legend>{t('create.type')}</legend>
          {[false, true].map((v) => (
            <label key={String(v)}>
              <input type="radio" name="type" checked={voice === v} onChange={() => setVoice(v)} /> {t(v ? 'create.voice' : 'create.text')}
            </label>
          ))}
        </fieldset>
        <ErrorLine>{message && (unlinked ? message : `${t('create.failed')} ${message}`)}</ErrorLine>
        <DialogButtons>
          <Button variant="text" onClick={onClose}>
            {t('common.close')}
          </Button>
          <Button variant="text" type="submit" disabled={!name.trim() || busy}>
            {busy ? t('create.creating') : unlinked ? t('create.addToSpace') : t('create.create')}
          </Button>
        </DialogButtons>
      </form>
    </Dialog>
  );
}

/** The full form: Home's "Create channel" page and the "Create space" page (09, "The full form"). */
export function CreateForm({ client, kind, onState }: { client: MatrixClient; kind: 'channel' | 'space'; onState?: (state: { busy: boolean; touched: boolean }) => void }) {
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');
  const [encrypted, setEncrypted] = useState(true);
  const [access, setAccess] = useState<Access>('circle');
  const { run, busy, message, unlinked } = useCreate(client, async (roomId, stayed) => {
    if (kind === 'channel') return void (stayed && opened(undefined, roomId));
    // A new space's first channel (09, Making the room 5): the space opens on it, else on its lobby
    // with the error in friendly words (v1.58.17).
    try {
      const channel = await makeRoom(client, { name: t('create.general'), kind: 'text', encrypted: true, space: { roomId } });
      await link(client, roomId, channel);
      if (stayed) navigate(roomPath({ space: roomId }, channel));
    } catch (e) {
      if (stayed) navigate(`/${encodeURIComponent(roomId)}/lobby/`);
      toast(t('create.generalFailed', { reason: describe(e, 'save').text }));
    }
  });
  // Untouched: every field as it opened (07, Closing a form page: Escape closes only then).
  const touched = !!name || !!topic || !encrypted || access !== 'circle';
  useEffect(() => onState?.({ busy, touched }), [busy, touched, onState]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) void run({ name: name.trim(), topic: topic.trim() || undefined, kind: kind === 'space' ? 'space' : 'text', encrypted: kind === 'channel' && encrypted, access: kind === 'space' ? access : undefined });
  };
  const circle = circleName(client.getUserId()!);
  const choices: [Access, string | undefined, string, string][] = [
    ['circle', undefined, t('create.everyone', { circle }), t('create.everyoneLine', { circle })],
    ['invite', frameIcons.lock, t('create.private'), t('create.privateLine')],
    ['public', frameIcons.public, t('create.public'), t('create.publicLine')],
  ];
  return (
    <>
      {kind === 'space' && <CircleSpaces client={client} />}
      <form className="create-form" onSubmit={submit}>
        {kind === 'space' && (
          <>
            <h3 className="form-section">{t('create.access')}</h3>
            {choices.map(([value, badge, title, line]) => (
              <button key={value} type="button" className="access-card" aria-pressed={access === value} disabled={busy} onClick={() => setAccess(value)}>
                <span className="room-icon">
                  <Icon svg={frameIcons.gridView} size={24} />
                  {badge && <Icon svg={badge} size={13} className="room-icon-badge" />}
                </span>
                <span>
                  <strong>{title}</strong>
                  <span>{line}</span>
                </span>
                {access === value && <Icon svg={icons.check} size={20} />}
              </button>
            ))}
          </>
        )}
        <Field label={t('create.name')} value={name} required maxLength={MAX} count={`${name.length}/${MAX}`} autoComplete="off" disabled={busy} autoFocus onChange={(e) => setName(e.target.value)} />
        <div className="field">
          <textarea id="create-topic" placeholder=" " value={topic} disabled={busy} aria-label={t('create.topic')} onChange={(e) => setTopic(e.target.value)} />
          <label htmlFor="create-topic">{t('create.topicOptional')}</label>
        </div>
        {kind === 'channel' && (
          <>
            <h3 className="form-section">{t('create.options')}</h3>
            <Switch label={t('create.encryption')} text={t('create.encryptionText')} checked={encrypted} disabled={busy} onChange={setEncrypted} />
          </>
        )}
        <ErrorLine>{message}</ErrorLine>
        <Button variant="filled" type="submit" busy={busy} className="wide">
          {busy ? t('create.creating') : unlinked ? t('create.addToSpace') : t('create.create')}
        </Button>
      </form>
    </>
  );
}

let openDialog: ((space: Room) => void) | undefined;
/** Opens the short dialog for a space (from a category's "+", the space menu, the list, the lobby). */
export const createChannelIn = (space: Room) => openDialog?.(space);
/** May the person add channels to this space (send m.space.child)? */
export const mayAddChannels = (client: MatrixClient, space: Room) => space.currentState.maySendStateEvent(EventType.SpaceChild, client.getUserId()!);

/** Where the short dialog lives in the frame. */
export function CreateHost({ client }: { client: MatrixClient }) {
  const [space, setSpace] = useState<Room>();
  useEffect(() => {
    openDialog = setSpace;
    return () => void (openDialog = undefined);
  }, []);
  return space ? <CreateChannelDialog client={client} space={space} onClose={() => setSpace(undefined)} /> : null;
}
